// eslint-disable-next-line no-restricted-imports
import { Chip as MuiChip, createStyles, makeStyles, PropTypes, Theme } from '@material-ui/core';
import React from 'react';

type StandardColor = Exclude<PropTypes.Color, 'inherit' | 'default'>;
type ExtraColor = 'success' | 'error' | 'warning' | 'info';
type ChipColor = StandardColor | ExtraColor;

const useStyleColor = makeStyles<
  Theme,
  { color: ChipColor },
  'overrideColorOutlined' | 'overrideColorDefault'
>((theme) =>
  createStyles({
    overrideColorOutlined: {
      color: ({ color }) => theme['palette'][color].main,
      borderColor: ({ color }) => theme['palette'][color].main,
      backgroundColor: 'transparent',
      '&> svg': {
        color: ({ color }) => theme['palette'][color].main,
      },
    },
    overrideColorDefault: {
      color: 'white',
      borderColor: ({ color }) => theme['palette'][color].contrastText,
      backgroundColor: ({ color }) => theme['palette'][color].main,
      '&> svg': {
        color: 'rgba(255,255,255,0.6)',
      },
    },
  }),
);

const useStyleDefault = makeStyles<
  Theme,
  Record<string, never>,
  'overrideOutlined' | 'overrideDefault'
>((theme) =>
  createStyles({
    overrideOutlined: {
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      borderColor: theme.palette.grey[400],
      '&> svg': {
        color: theme.palette.grey[400],
      },
    },
    overrideDefault: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.grey[400],
      '&> svg': {
        color: 'rgba(0, 0, 0, 0.23)',
      },
    },
  }),
);

const DefaultChip: React.FC<Omit<ChipProps, 'color'>> = ({ variant, ...props }) => {
  const { overrideDefault, overrideOutlined } = useStyleDefault({});
  const className = variant === 'outlined' ? overrideOutlined : overrideDefault;

  return <MuiChip {...props} variant={variant} className={className} />;
};

type ColorChipProps = Omit<React.ComponentProps<typeof MuiChip>, 'color'> & {
  color: ChipColor;
};

const ColorChip: React.FC<ColorChipProps> = ({ color, variant, ...props }) => {
  const { overrideColorOutlined, overrideColorDefault } = useStyleColor({ color });
  const className = variant === 'outlined' ? overrideColorOutlined : overrideColorDefault;

  return <MuiChip {...props} variant={variant} className={className} />;
};

export type ChipProps = Omit<React.ComponentProps<typeof MuiChip>, 'color'> & {
  color?: ChipColor | 'default';
};

export const Chip: React.FC<ChipProps> = ({ color = 'default', ...props }) => {
  if (color === 'default') {
    return <DefaultChip {...props} />;
  }
  return <ColorChip {...props} color={color} />;
};
